import React from "react";
import {
  Box,
  Typography,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CustomerIcon from "../../assets/icons/customer_icon.svg";
import CustomDivider from "../common/CustomDivider";
import { Button } from "@mui/material";

export interface NextStepBannerProps {
  managerName?: string;
  estimatedTime?: string;
  status: string;
  onResume?: () => void;
  completedSteps?: number;
  totalSteps?: number;
  isResumeLoading?: boolean;
}

const NextStepBanner: React.FC<NextStepBannerProps> = ({
  managerName = "",
  status = "",
  estimatedTime = "",
  onResume,
  completedSteps = 0,
  totalSteps = 6,
  isResumeLoading = false,
}) => {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("sm"));

  const showTitleAndDesc = (status: string) => {
    switch (status) {
      case "Application Submitted":
        return {
          title: "Application Submitted: What Happens Next?",
          description: managerName
            ? <>You're one step closer to achieving your educational goals. Your dedicated Relationship Manager <strong>{managerName}</strong> will connect with you soon to guide you through the remaining steps and answer all your queries.</>
            : "You're one step closer to achieving your educational goals. Your dedicated Relationship Manager will connect with you soon to guide you through the remaining steps and answer all your queries.",
        };
      case "Submission Pending":
      case "PROVISIONALGENERATED":
        return {
          title: "You're Almost There — Complete Your Loan Application",
          description: `You're just a few steps away from completing your loan application. Pick up right where you left off and submit your application today.`,
        };
      case "In-Progress":
        return {
          title: "Progress Update: We're Working On Your Loan Application",
          description: managerName
            ? <>Your dedicated Relationship Manager <strong>{managerName}</strong> is processing your loan application details and will reach out directly if any additional information or documentation is needed.</>
            : "Your dedicated Relationship Manager is processing your loan application details and will reach out directly if any additional information or documentation is needed.",
        };
      case "Review In-Progress":
        return {
          title: "Your Loan Application Is Under Review",
          description: `Our team is actively reviewing your loan application details. We're carefully checking your details to ensure a smooth process. Sit tight - we'll send you an update the moment we finish.`,
        };
      case "Underwriting In-Progress":
        return {
          title: "Almost There! Your Loan Application Is Under Review",
          description: `Our team is actively reviewing your loan application details. We're carefully checking your details to ensure a smooth process. Sit tight - we'll send you an update the moment we finish.`,
        };
      case "Application Rejected":
      case "Declined by Credit":
        return {
          title: "Application Status Update",
          description: `After a thorough review, we regret to inform you that we are unable to process your loan application at this time. We look forward to serving you in the future.`,
        };
      case "Application Closed":
        return {
          title: "Status Update: Application Closed",
          description: `This application is now closed. For any questions or guidance on your next steps, please connect with your Relationship Manager.`,
        };
      case "Sanctioned":
      case "APPROVED":
        return {
          title: "Congratulations! Your Education Loan Has Been Sanctioned",
          description: managerName
            ? `Your education loan application has been officially sanctioned. Your Relationship Manager will connect with you shortly to update you on the next steps.`
            : "Great news! Your application has been sanctioned. Your Sales Manager will provide you with the next steps.",
        };
      case "Disbursed":
        return {
          title: "Congratulations! Your Education Loan Has Been Disbursed Successfully!",
          description: `Your funds have been successfully transferred! We're thrilled to support your education journey and wish you all the best.`,
        };
      default:
        return {
          title: "",
          description: "",
        };
    }
  };

  const { title, description } = showTitleAndDesc(status);

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#EBE9FE",
        borderRadius: "12px",
        borderLeft: "5px solid #635BFF",
        p: { xs: 1, sm: 1.5, md: 2 },
        boxSizing: "border-box",
        mb: { xs: 1, sm: 2 },
      }}
    >
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={{ xs: 2.5, lg: 3 }}
        alignItems={{ xs: "stretch", lg: "center" }}
        justifyContent="space-between"
      >
        {/* Left Section: Icon & Message */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <img
              src={CustomerIcon}
              alt="Customer Icon"
              style={{ width: 42, height: 42 }}
            />
          </Box>
          <Box sx={{ textAlign: "left" }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: "#7367F0",
                fontSize: { xs: "0.95rem", sm: "13px" },
                lineHeight: 1.3,
                mb: 0.5,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: "#00000096",
                fontSize: { xs: "0.825rem", sm: "13px" },
                lineHeight: 1.4,
              }}
            >
              {description}
            </Typography>
          </Box>
        </Stack>

        {onResume && (
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0, width: { xs: "100%", lg: "auto" } }}>
            <Box
              sx={{
                border: "1px solid #808390",
                borderRadius: "32px",
                px: 1.5,
                py: 0.5,
                color: "#00000073",
                fontSize: "12px",
                fontweight: 500,
                whiteSpace: "nowrap",
                backgroundColor: "#FFFFFF",
              }}
            >
              {completedSteps} of {totalSteps} steps done
            </Box>
            {!isMobileView && (
              <Button
                variant="contained"
                onClick={onResume}
                disabled={isResumeLoading}
                sx={{
                  height: "30px",
                  width: "140px",
                  borderRadius: "8px",
                  backgroundColor: "#fecd3d",
                  color: "#161616",
                  fontWeight: 700,
                  textTransform: "none",
                  boxShadow: "none",
                  fontSize: "12px",
                  padding: "6px 8px",
                  "&:hover": { backgroundColor: "#fecd3d", boxShadow: "none" },
                }}
              >
                {isResumeLoading ? <CircularProgress size={20} sx={{ color: "#161616" }} /> : "Resume Application"}
              </Button>
            )}
          </Stack>
        )}

        {/* {isMobileView && status === "Application Submitted" && (
          <CustomDivider />
        )} */}

        {/* Right Section: Time & Notifications */}
        {/* {status === "Application Submitted" && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 2, sm: 3 }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ flexShrink: 0 }}
          >
            <Stack
              direction="row"
              spacing={isMobileView ? 2 : 1.5}
              alignItems="center"
            >
              <Box
                sx={{
                  backgroundColor: "#DCD7FE",
                  borderRadius: "8px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AccessTimeIcon sx={{ color: "#00000096", fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ color: "#00000096", fontWeight: 500, lineHeight: 1.2 }}
                >
                  Estimated Connection Time
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{
                    color: "#00000096",
                    fontWeight: 500,
                    textAlign: "left",
                    lineHeight: 1.2,
                  }}
                >
                  {estimatedTime}
                </Typography>
              </Box>
            </Stack>

            {isMobileView && <CustomDivider />}

            {isMobileView && (
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  borderColor: "#C7C1F9",
                  display: { xs: "none", sm: "block" },
                  my: 0.5,
                }}
              />
            )}

            <Stack
              direction="row"
              spacing={isMobileView ? 2 : 1.5}
              alignItems="center"
            >
              <Box
                sx={{
                  backgroundColor: "#DCD7FE",
                  borderRadius: "8px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MailOutlineIcon sx={{ color: "#00000096", fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ color: "#00000096", fontWeight: 500, lineHeight: 1.2 }}
                >
                  You will also receive updates
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{
                    color: "#00000096",
                    fontWeight: 500,
                    textAlign: "left",
                    lineHeight: 1.2,
                  }}
                >
                  on Email & SMS
                </Typography>
              </Box>
            </Stack>
          </Stack>
        )} */}
      </Stack>
    </Box>
  );
};

export default NextStepBanner;
